/**
 * Sort control (collection / search toolbar)
 *
 * The sort <select> is styled with `appearance: none` (we draw our own caret), which makes its
 * intrinsic width collapse below the selected option's text — so long labels ("Alphabetically,
 * A-Z") were getting clipped at narrower desktop widths. This element sizes the select to the
 * width of its *selected* option, so the box always fits the visible label with no dead space
 * before the caret. Re-measures on change and after the AJAX grid swap (the toolbar re-renders
 * inside #CollectionAjaxContent, upgrading a fresh element each time).
 */
class SortControl extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.abortController = new AbortController();

    // Size the select before it's revealed, with the transition suppressed, so it doesn't slide
    // in from its unsized width on load. The transition is restored for later sort changes.
    this.select.style.transition = 'none';
    this.resize();
    requestAnimationFrame(() => {
      this.select.style.transition = '';
    });

    this.select.addEventListener('change', () => this.resize(), {
      signal: this.abortController.signal,
    });

    // Re-measure once the web font settles, so the first measure isn't taken against a fallback.
    document.fonts?.ready.then(() => this.resize());
  }

  resize() {
    const styles = getComputedStyle(this.select);
    this.ctx ??= document.createElement('canvas').getContext('2d');
    if (!this.ctx) return; // no canvas 2d — leave the select at its CSS width
    this.ctx.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
    // Match the select's letter-spacing (.gram = -0.04rem) so the measure tracks the rendered
    // width; browsers without canvas letterSpacing ignore it and fall back to the font-only measure.
    this.ctx.letterSpacing = styles.letterSpacing;
    const label = this.select.options[this.select.selectedIndex]?.text ?? '';
    // +2px so the text never sits flush against the (separately drawn) caret.
    this.select.style.width = `${Math.ceil(this.ctx.measureText(label).width) + 2}px`;
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

if (!customElements.get('sort-control')) {
  customElements.define('sort-control', SortControl);
}
